import Button from 'components/Button';
import { Form, InputField, RadioField, SelectField } from 'components/Form';
import { SmartContainer, SmartInline, SmartItem } from 'components/SmartContainer/SmartContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GenericFilterOperationEnum } from 'services/tenantManagementService';
import { additionalFilterOptions, AndOrEnum, findAllFilters } from '../../filtersHelper';
import { StringFilterModel } from './StringFilterModel';
import { getFormatedId, getUnformattedId } from 'utils/commonHelper';
import { FormattedReferenceColumnModel, StringColumnModel, FilterType, TextareaColumnModel } from 'components/Table';

type Props = {
	column: StringColumnModel | FormattedReferenceColumnModel | TextareaColumnModel
	filters: FilterType[]
	onSave: (filters: FilterType[]) => void
	onCancel: () => void
}

const filterStringTypeOptions = [
	{ value: GenericFilterOperationEnum.EQUALS, text: 'i18n.label.filter.operation.equals' },
	{ value: GenericFilterOperationEnum.LIKE, text: 'i18n.label.filter.operation.like' },
	{ value: GenericFilterOperationEnum.LIKE_STARTSWITH, text: 'i18n.label.filter.operation.likeStartsWith' },
	{ value: GenericFilterOperationEnum.LIKE_ENDSWITH, text: 'i18n.label.filter.operation.likeEndsWith' },
	{ value: GenericFilterOperationEnum.NOT_EQUALS, text: 'i18n.label.filter.operation.notEquals' },
]

// value will be number, so be carefull to have operations that are used for both string and number only
const filterFormattedReferenceTypeOptions = [
	{ value: GenericFilterOperationEnum.EQUALS, text: 'i18n.label.filter.operation.equals' },
	{ value: GenericFilterOperationEnum.NOT_EQUALS, text: 'i18n.label.filter.operation.notEquals' }
]

export const StringFilter = ({ column, filters, onSave, onCancel }: Props) => {
	const [values, setValues] = useState<StringFilterModel>(new StringFilterModel());

	const filterTypeOptions = useMemo(
		() => column instanceof FormattedReferenceColumnModel ? filterFormattedReferenceTypeOptions : filterStringTypeOptions,
		[column]
	)

	useEffect(
		() => {
			const newValues = new StringFilterModel();

			const currentFilters = findAllFilters(filters, column.id);
			// if there are two filters that means AND filter is used
			if (currentFilters && currentFilters.length === 2) {
				newValues.additionalFilter = AndOrEnum.And;
				newValues.filterType1 = currentFilters[0].operation;
				newValues.filterValue1 = column instanceof FormattedReferenceColumnModel ? getFormatedId(column.entityPrefix, currentFilters[0].value) : currentFilters[0].value;
				newValues.filterType2 = currentFilters[1].operation;
				newValues.filterValue2 = column instanceof FormattedReferenceColumnModel ? getFormatedId(column.entityPrefix, currentFilters[1].value) : currentFilters[1].value;
				// if there is one filter, but without OR filters
			} else if (currentFilters && currentFilters.length === 1 && (!currentFilters[0].orFilters || currentFilters[0].orFilters.length === 0)) {
				newValues.additionalFilter = AndOrEnum.And;
				newValues.filterType1 = currentFilters[0].operation;
				newValues.filterValue1 = column instanceof FormattedReferenceColumnModel ? getFormatedId(column.entityPrefix, currentFilters[0].value) : currentFilters[0].value;
				// if there is one filter, with two OR filters
			} else if (currentFilters && currentFilters.length === 1 && currentFilters[0].orFilters && currentFilters[0].orFilters.length === 2) {
				newValues.additionalFilter = AndOrEnum.Or;
				newValues.filterType1 = currentFilters[0].orFilters[0].operation
				newValues.filterValue1 = column instanceof FormattedReferenceColumnModel ? getFormatedId(column.entityPrefix, currentFilters[0].orFilters[0].value) : currentFilters[0].orFilters[0].value;
				newValues.filterType2 = currentFilters[0].orFilters[1].operation
				newValues.filterValue2 = column instanceof FormattedReferenceColumnModel ? getFormatedId(column.entityPrefix, currentFilters[0].orFilters[1].value) : currentFilters[0].orFilters[1].value;
				// if there is one filter, with one OR filters
			} else if (currentFilters && currentFilters.length === 1 && currentFilters[0].orFilters && currentFilters[0].orFilters.length === 1) {
				newValues.additionalFilter = AndOrEnum.Or;
				newValues.filterType1 = currentFilters[0].orFilters[0].operation
				newValues.filterValue1 = column instanceof FormattedReferenceColumnModel ? getFormatedId(column.entityPrefix, currentFilters[0].orFilters[0].value) : currentFilters[0].orFilters[0].value;
			}

			setValues(newValues);
		},
		[filters, column]
	)

	const submitCallback = useCallback(
		async () => {
			let newFilters = [...filters];
			// remove current filters with specific columnId
			const currentFilters = findAllFilters(newFilters, column.id);
			if (currentFilters.length > 0) {
				currentFilters.forEach(filter => {
					const index = newFilters.indexOf(filter);
					newFilters.splice(index, 1);
				})
			}

			const newFilter: FilterType = {};
			// If OR filter is selected, add filters to orFilters array
			if (values.additionalFilter === AndOrEnum.Or) {
				newFilter.orFilters = []
				if (values.filterType1) {
					newFilter.orFilters.push({
						columnId: column.id,
						operation: values.filterType1,
						value: column instanceof FormattedReferenceColumnModel ? (getUnformattedId(column.entityPrefix, values.filterValue1) || '') : values.filterValue1
					})
				}

				if (values.filterType2) {
					newFilter.orFilters.push({
						columnId: column.id,
						operation: values.filterType2,
						value: column instanceof FormattedReferenceColumnModel ? (getUnformattedId(column.entityPrefix, values.filterValue2) || '') : values.filterValue2
					})
				}
				if (newFilter.orFilters.length > 0) {
					newFilters.push(newFilter);
				}
			}
			// If AND filter is selected add "Parent" filter
			if (values.additionalFilter === AndOrEnum.And) {
				if (values.filterType1) {
					newFilters.push({
						columnId: column.id,
						operation: values.filterType1,
						value: column instanceof FormattedReferenceColumnModel ? (getUnformattedId(column.entityPrefix, values.filterValue1) || '') : values.filterValue1
					})
				}

				// If second filter is selected add new "Parent" filter
				if (values.filterType2) {
					newFilters.push({
						columnId: column.id,
						operation: values.filterType2,
						value: column instanceof FormattedReferenceColumnModel ? (getUnformattedId(column.entityPrefix, values.filterValue2) || '') : values.filterValue2
					})
				}
			}

			onSave && await onSave(newFilters);
		},
		[filters, column, onSave, values]
	)

	const clearCallback = useCallback(
		() => {
			setValues(new StringFilterModel());
		},
		[]
	)

	return (
		<Form
			values={values}
			onChange={setValues}
			onSubmit={submitCallback}
			onCancel={onCancel}
			submitButtonText='i18n.button.apply'
			disableUnsavedChangesGuard
			render={() => (
				<SmartContainer>
					<SmartItem>
						<SmartInline>
							<SelectField
								id='filterType1'
								items={filterTypeOptions}
								getItemId={(item) => item.value}
								getItemText={(item) => item.text}
								inline
							/>
							<InputField
								id='filterValue1'
								inline
							/>
						</SmartInline>
						<RadioField
							id='additionalFilter'
							items={additionalFilterOptions}
						/>
						<SmartInline>
							<SelectField
								id='filterType2'
								items={filterTypeOptions}
								getItemId={(item) => item.value}
								getItemText={(item) => item.text}
								inline
							/>
							<InputField
								id='filterValue2'
								inline
							/>
						</SmartInline>
					</SmartItem>
				</SmartContainer>
			)}
			renderAdditionalButtons={() => (
				<Button
					text='i18n.button.clear'
					color='destructive'
					onClick={clearCallback}
				/>
			)}
		/>
	)
}
